// filepath: /client/src/components/MandatoryDiscountsManager.js
import React, { useState } from 'react';
import { MANDATORY_DISCOUNT_TYPES } from '../enums/mandatory_discounts';

const MandatoryDiscountsManager = ({ 
  discounts, 
  setDiscounts, 
  disabled = false,
  showPreview = true,
  maxCapacity = null
}) => {
  const [newDiscount, setNewDiscount] = useState({
    type: MANDATORY_DISCOUNT_TYPES.SENIOR,
    id_number: '',
    guest_name: ''
  });
  const [errors, setErrors] = useState({});

  const capacityLimit = maxCapacity != null ? parseInt(maxCapacity, 10) : null;
  const atCapacity = capacityLimit != null && !Number.isNaN(capacityLimit) && discounts.length >= capacityLimit;

  const validateDiscount = (discount) => {
    const errors = {};
    
    if (!discount.type || discount.type === MANDATORY_DISCOUNT_TYPES.NONE) {
      errors.type = 'Please select a discount type';
    }
    
    if (!discount.id_number || discount.id_number.trim() === '') {
      errors.id_number = 'ID number is required';
    }
    
    if (!discount.guest_name || discount.guest_name.trim() === '') {
      errors.guest_name = 'Guest name is required';
    }
    
    return errors;
  };

  const addDiscount = () => {
    if (atCapacity) {
      setErrors({
        capacity: `Up to ${capacityLimit} SC/PWD discount${capacityLimit === 1 ? '' : 's'} for this room.`
      });
      return;
    }

    const validationErrors = validateDiscount(newDiscount);
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    // Check for duplicate ID numbers
    const existingDiscount = discounts.find(d => d.id_number === newDiscount.id_number);
    if (existingDiscount) {
      setErrors({ id_number: 'This ID number has already been added' });
      return;
    }
    
    setDiscounts([...discounts, { ...newDiscount, id: Date.now() }]);
    setNewDiscount({
      type: MANDATORY_DISCOUNT_TYPES.SENIOR,
      id_number: '',
      guest_name: ''
    });
    setErrors({});
  };

  const removeDiscount = (id) => {
    setDiscounts(discounts.filter(d => d.id !== id));
    setErrors((prev) => {
      if (!prev.capacity) return prev;
      const { capacity, ...rest } = prev;
      return rest;
    });
  };

  const updateDiscount = (id, field, value) => {
    setDiscounts(discounts.map(d => 
      d.id === id ? { ...d, [field]: value } : d
    ));
  };

  const getDiscountLabel = (type) => {
    switch (type) {
      case MANDATORY_DISCOUNT_TYPES.SENIOR:
        return 'Senior Citizen (20% + VAT Exempt)';
      case MANDATORY_DISCOUNT_TYPES.PWD:
        return 'Person with Disability (20% + VAT Exempt)';
      default:
        return '';
    }
  };

  return (
    <div
      data-testid="mandatory-discounts-manager"
      style={{ 
      marginTop: 16, 
      padding: '15px', 
      border: '1px solid #ddd', 
      borderRadius: '8px', 
      backgroundColor: '#f9f9f9' 
    }}>
      <h4 style={{ margin: '0 0 15px 0', color: '#2a3b5c', fontSize: '16px' }}>
        Mandatory Discounts (SC/PWD)
      </h4>

      {capacityLimit != null && !Number.isNaN(capacityLimit) && (
        <div
          data-testid="scpwd-capacity-hint"
          style={{
          fontSize: '13px',
          color: atCapacity ? '#721c24' : '#555',
          marginBottom: '12px',
          padding: '8px 10px',
          backgroundColor: atCapacity ? '#f8d7da' : '#eef3f8',
          border: `1px solid ${atCapacity ? '#f5c6cb' : '#d0d7de'}`,
          borderRadius: '4px'
        }}>
          Up to {capacityLimit} SC/PWD discount{capacityLimit === 1 ? '' : 's'} for this room
          {discounts.length > 0 ? ` (${discounts.length}/${capacityLimit} applied)` : ''}.
        </div>
      )}
      
      {/* Existing Discounts List */}
      {discounts.length > 0 && (
        <div style={{ marginBottom: '15px' }}>
          {discounts.map((discount) => (
            <div key={discount.id} style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '10px', 
              marginBottom: '10px',
              padding: '10px',
              backgroundColor: '#fff',
              borderRadius: '4px',
              border: '1px solid #e3e3e3'
            }}>
              <div style={{ flex: 1 }}>
                <select
                  value={discount.type}
                  onChange={(e) => updateDiscount(discount.id, 'type', e.target.value)}
                  disabled={disabled}
                  style={{ 
                    width: '100%', 
                    padding: '5px', 
                    border: '1px solid #ddd', 
                    borderRadius: '4px' 
                  }}
                >
                  <option value={MANDATORY_DISCOUNT_TYPES.SENIOR}>Senior Citizen</option>
                  <option value={MANDATORY_DISCOUNT_TYPES.PWD}>Person with Disability</option>
                </select>
              </div>
              
              <div style={{ flex: 1 }}>
                <input
                  type="text"
                  placeholder="ID Number *"
                  value={discount.id_number}
                  onChange={(e) => updateDiscount(discount.id, 'id_number', e.target.value)}
                  disabled={disabled}
                  required
                  style={{ 
                    width: '100%', 
                    padding: '5px', 
                    border: '1px solid #ddd', 
                    borderRadius: '4px' 
                  }}
                />
              </div>
              
              <div style={{ flex: 1 }}>
                <input
                  type="text"
                  placeholder="Guest Name *"
                  value={discount.guest_name}
                  onChange={(e) => updateDiscount(discount.id, 'guest_name', e.target.value)}
                  disabled={disabled}
                  required
                  style={{ 
                    width: '100%', 
                    padding: '5px', 
                    border: '1px solid #ddd', 
                    borderRadius: '4px' 
                  }}
                />
              </div>
              
              {!disabled && (
                <button
                  type="button"
                  onClick={() => removeDiscount(discount.id)}
                  style={{
                    background: '#ff4444',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '5px 10px',
                    cursor: 'pointer',
                    fontSize: '12px'
                  }}
                >
                  Remove
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add New Discount Form */}
      {!disabled && (
        <div style={{ 
          padding: '10px',
          backgroundColor: '#fff',
          borderRadius: '4px',
          border: '1px solid #e3e3e3',
          opacity: atCapacity ? 0.65 : 1
        }}>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              <select
                value={newDiscount.type}
                onChange={(e) => setNewDiscount({ ...newDiscount, type: e.target.value })}
                disabled={atCapacity}
                style={{ 
                  width: '100%', 
                  padding: '8px', 
                  border: errors.type ? '1px solid #ff4444' : '1px solid #ddd', 
                  borderRadius: '4px' 
                }}
              >
                <option value={MANDATORY_DISCOUNT_TYPES.SENIOR}>Senior Citizen</option>
                <option value={MANDATORY_DISCOUNT_TYPES.PWD}>Person with Disability</option>
              </select>
              {errors.type && (
                <div style={{ color: '#ff4444', fontSize: '12px', marginTop: '2px' }}>
                  {errors.type}
                </div>
              )}
            </div>
            
            <div style={{ flex: 1 }}>
              <input
                type="text"
                data-testid="scpwd-new-id"
                placeholder="ID Number *"
                value={newDiscount.id_number}
                onChange={(e) => setNewDiscount({ ...newDiscount, id_number: e.target.value })}
                required
                disabled={atCapacity}
                style={{ 
                  width: '100%', 
                  padding: '8px', 
                  border: errors.id_number ? '1px solid #ff4444' : '1px solid #ddd', 
                  borderRadius: '4px' 
                }}
              />
              {errors.id_number && (
                <div style={{ color: '#ff4444', fontSize: '12px', marginTop: '2px' }}>
                  {errors.id_number}
                </div>
              )}
            </div>
            
            <div style={{ flex: 1 }}>
              <input
                type="text"
                data-testid="scpwd-new-guest-name"
                placeholder="Guest Name *"
                value={newDiscount.guest_name}
                onChange={(e) => setNewDiscount({ ...newDiscount, guest_name: e.target.value })}
                required
                disabled={atCapacity}
                style={{ 
                  width: '100%', 
                  padding: '8px', 
                  border: errors.guest_name ? '1px solid #ff4444' : '1px solid #ddd', 
                  borderRadius: '4px' 
                }}
              />
              {errors.guest_name && (
                <div style={{ color: '#ff4444', fontSize: '12px', marginTop: '2px' }}>
                  {errors.guest_name}
                </div>
              )}
            </div>
            
            <button
              type="button"
              data-testid="scpwd-add-discount"
              onClick={addDiscount}
              disabled={atCapacity}
              style={{
                background: atCapacity ? '#9bb8e8' : '#4f8cff',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                padding: '8px 15px',
                cursor: atCapacity ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                whiteSpace: 'nowrap'
              }}
            >
              Add
            </button>
          </div>
          {errors.capacity && (
            <div style={{ color: '#ff4444', fontSize: '12px', marginTop: '8px' }}>
              {errors.capacity}
            </div>
          )}
        </div>
      )}

      {/* Discount Preview */}
      {showPreview && discounts.length > 0 && (
        <div style={{ 
          marginTop: '15px', 
          padding: '10px', 
          backgroundColor: '#e8f4fd', 
          borderRadius: '4px',
          border: '1px solid #4f8cff'
        }}>
          <div style={{ fontSize: '14px', color: '#2a3b5c', fontWeight: '600' }}>
            Applied Discounts Summary:
          </div>
          {discounts.map((discount) => (
            <div key={discount.id} style={{ fontSize: '13px', color: '#555', marginTop: '5px' }}>
              • {getDiscountLabel(discount.type)} - {discount.guest_name} ({discount.id_number})
            </div>
          ))}
          <div style={{ 
            fontSize: '12px', 
            color: '#666', 
            marginTop: '8px', 
            fontStyle: 'italic' 
          }}>
            Note: Each discount provides 20% off + VAT exemption calculated per person based on room capacity.
          </div>
        </div>
      )}
    </div>
  );
};

export default MandatoryDiscountsManager;
